import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Building2, LogOut, ArrowRight, ArrowLeft, BarChart3, Car, Zap, TrendingUp, PackageCheck, Percent } from "lucide-react";

const Card = ({ icon: Icon, title, desc, onClick, testid, accent }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group text-left rounded-2xl border border-border bg-card p-8 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 active:scale-[0.99]"
  >
    <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${accent}`}>
      <Icon className="h-7 w-7 text-white" />
    </div>
    <h2 className="font-heading text-2xl font-semibold tracking-tight mt-6">{title}</h2>
    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
    <div className="flex items-center gap-1.5 text-sm font-medium text-primary mt-6 group-hover:gap-2.5 transition-all">
      Openen <ArrowRight className="h-4 w-4" />
    </div>
  </button>
);

export default function RapportenHub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const reports = [
    { testid: "rap-opbrengsten", icon: TrendingUp, accent: "bg-emerald-600", title: "Opbrengsten",
      desc: "Alle inkomsten uit het dagboek van ontvangsten, verdeeld tussen zonder btw, btw en met btw — per tarief en per vestiging.",
      to: "/rapporten/opbrengsten" },
    { testid: "rap-diverse-opbrengsten", icon: PackageCheck, accent: "bg-teal-600", title: "Diverse opbrengsten",
      desc: "Vergoedingen van de pakjesdiensten (GLS, DPD, bpost, PostNL …), uitgesplitst per bedrijf en per maand.",
      to: "/rapporten/diverse" },
    { testid: "rap-marge", icon: Percent, accent: "bg-violet-600", title: "Marge per type",
      desc: "Verkopen (dagboek) t.o.v. aankopen handelsgoederen per btw-type, met marge-% (Tabak 0%, Voeding 6%, Alcohol 21%, Lotto).",
      to: "/rapporten/marge" },
    { testid: "rap-aankopen", icon: BarChart3, accent: "bg-indigo-600", title: "Overzicht aankopen",
      desc: "Aankopen per kostencategorie en, voor handelsgoederen, de maatstaf per btw-tarief. Met exportrapporten.",
      to: "/rapporten/overzicht" },
    { testid: "rap-autokosten", icon: Car, accent: "bg-amber-600", title: "Autokosten",
      desc: "Autokosten per voertuig (GLE / Vito) en per soort (leasing / onderhoud), met export.",
      to: "/rapporten/autokosten" },
    { testid: "rap-energie", icon: Zap, accent: "bg-sky-600", title: "Energie",
      desc: "Energiekosten per vestiging (Brustem / De Leesberg), gesplitst in aardgas en elektriciteit, per maand.",
      to: "/rapporten/energie" },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="rapporten-hub">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-semibold text-sm">Singh Express Shop BV</div>
              <div className="text-xs text-muted-foreground">BE 0788.952.072</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" data-testid="hub-logout" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Afmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" data-testid="rap-back-boekhouding" onClick={() => navigate("/boekhouding")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Boekhouding
        </Button>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Rapporten</h1>
        <p className="text-muted-foreground mt-2">Overzichten van opbrengsten en aankopen.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {reports.map((r) => (
            <Card key={r.testid} testid={r.testid} icon={r.icon} accent={r.accent} title={r.title} desc={r.desc} onClick={() => navigate(r.to)} />
          ))}
        </div>
      </main>
    </div>
  );
}
